'use client'

import type { ReactNode } from 'react'
import { RowSurfaceProvider } from '@/deskUiSkeletons/rowTemplates/RowSurfaceContext'
import type { RowSkin, RowSurface } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'
import { SubRow } from '@/deskUiSkeletons/subs/SubRow'
import { showQuickReplyModal } from '@/deskUiSkeletons/Shared/QuickReplyModal'
import { defaultBlockedPreviewSkin } from '@/msgComponents/skins/blockedPreviewSkin'
import { rowVariantRegistry } from './RowTemplateRegistry'
import { nestedTemplateVariant } from './RowTemplateConfig'

const wikiRowSkin: RowSkin = {
  row: {
    base: 'min-w-0 list-none text-[var(--wiki-text)]',
    hidden: 'hidden',
    checked: 'opacity-80',
    foreign: 'opacity-95',
    foreignFrom: 'mb-1 text-xs text-[var(--wiki-muted)] [&_a]:text-[var(--wiki-link)]',
  },
  blockedNotice: { ...defaultBlockedPreviewSkin },
  icon: {
    base: 'inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded border align-middle',
    checkOn: 'border-[#36c] bg-[#36c]',
    checkOff: 'border-[var(--wiki-border)] bg-[var(--wiki-bg)]',
    favOn: 'border-amber-400 bg-amber-300',
    favOff: 'border-[var(--wiki-border)] bg-[var(--wiki-bg)]',
    qrOn: 'border-[#36c] bg-[var(--wiki-subtle)]',
    qrOff: 'border-[var(--wiki-border)] bg-[var(--wiki-bg)]',
  },
  titleLink: 'font-normal text-[var(--wiki-link)] no-underline hover:underline',
  timeWrap: 'whitespace-nowrap text-xs text-[var(--wiki-muted)]',
  timeText: 'whitespace-nowrap text-xs text-[var(--wiki-muted)]',
  text: 'min-w-0 whitespace-normal text-[15px] leading-7 text-[var(--wiki-text)]',
  metaLink: 'cursor-pointer text-sm text-[var(--wiki-link)] hover:underline',
  metaMore: 'cursor-pointer text-sm text-[var(--wiki-link)] hover:underline',
  meta: 'text-xs text-[var(--wiki-muted)]',
  feed: 'mt-2 flex list-none flex-col gap-1 p-0',
  folder: {
    row: 'flex flex-col gap-1 border-b border-[var(--wiki-border)] bg-[var(--wiki-bg)] px-1 py-3',
    head: 'flex min-w-0 items-center gap-2',
    title: 'min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm',
    time: 'flex-none text-xs text-[var(--wiki-muted)]',
    text: 'overflow-hidden text-ellipsis whitespace-nowrap text-sm text-[var(--wiki-muted)]',
    counts: 'flex gap-3 text-xs text-[var(--wiki-muted)]',
    count: 'whitespace-nowrap',
  },
}

const wikiRowSurface: RowSurface = {
  SubRow,
  showQuickReplyModal,
  variants: rowVariantRegistry,
  config: { nestedTemplateVariant },
  skin: wikiRowSkin,
}

export function WikiRowSurfaceProvider({ children }: { children: ReactNode }) {
  return <RowSurfaceProvider value={wikiRowSurface}>{children}</RowSurfaceProvider>
}
